import { useContext, useLayoutEffect, useMemo } from 'react';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { createTextStyle } from '@webapp/common/lib/ui';
import { CalcResultType } from '@webapp/common/resources/entities/survey-addons/tests';

import { selectAffectedQuestions, selectInfo, selectTimeAgo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';
import { scrollSurveyToTop } from '@webapp/survey/src/utils/scroll';

import { Answers, CompleteText, ResultsWidget } from './components';

import css from './test-results.css';

export const TestResult: VFC = () => {
    const {
        design: {
            fonts: { titleSize },
        },
        params,
    } = useStateSelector(selectInfo);
    const {
        testScore: { score, maxScore, percent, passed },
        promocode,
    } = useStateSelector((s) => s);
    const {
        main: { showDoneTest, showResultTest, completePageText = '' },
        tests: { maxScoreAmount, textDiapasons, calcResultType },
    } = params;

    const factPoints = score;
    const timeAgo = useStateSelector(selectTimeAgo);

    const {
        button,
        question: { text: questionTextStyles },
    } = useContext(CustomStylesCtx);

    const affectedQuestions = useStateSelector(selectAffectedQuestions);

    const selectedCompletePageText = useMemo(() => {
        const testResult = calcResultType === CalcResultType.PERCENTS ? percent : score;

        if (showResultTest && textDiapasons) {
            const crossDiapasonValues = textDiapasons.reduce((acc, item) => {
                if (acc[item.from]) {
                    acc[item.from] += 1;
                } else {
                    acc[item.from] = 1;
                }
                if (acc[item.to]) {
                    acc[item.to] += 1;
                } else {
                    acc[item.to] = 1;
                }

                return acc;
            }, {});

            return (
                textDiapasons.find((dia) => {
                    if (crossDiapasonValues[dia.to] > 1) {
                        return dia.from <= testResult && dia.to > testResult;
                    }

                    return dia.from <= testResult && dia.to >= testResult;
                })?.text || ''
            );
        }
        return completePageText;
    }, [showResultTest, completePageText, textDiapasons, score, calcResultType, percent]);

    const completePageTextWithPromocodes = useMemo(
        () => selectedCompletePageText.replace(/{promocode}/gi, promocode ?? ''),
        [selectedCompletePageText, promocode],
    );

    const titleStyle = createTextStyle({
        size: titleSize * 0.75,
    });

    useLayoutEffect(() => {
        scrollSurveyToTop();
    }, []);

    return (
        <div className={css.testResultsWrapper}>
            {!showResultTest && completePageTextWithPromocodes && (
                <CompleteText completePageText={completePageTextWithPromocodes} style={questionTextStyles} />
            )}
            {showResultTest && (
                <ResultsWidget
                    buttonStyles={button}
                    fact={factPoints}
                    maxScore={maxScoreAmount || maxScore}
                    params={params}
                    passed={passed}
                    percent={percent}
                    timeAgo={timeAgo}
                    titleStyle={titleStyle}
                    completeText={completePageTextWithPromocodes}
                />
            )}
            {showDoneTest && <Answers questions={affectedQuestions} />}
        </div>
    );
};
TestResult.displayName = 'TestResult';
